import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useGT } from "gt-react";
import {
  createVaultInApp,
  deleteVaultEntryInApp,
  exportVault,
  forgetRememberedVaultInApp,
  importVault,
  materializePendingVaultRefsInApp,
  refreshVaultInApp,
  lockVaultInApp,
  readVaultValue,
  saveVaultEntryInApp,
  unlockVaultInApp,
  useAppStore,
  useServersStore,
  useVaultStore,
  type SpuerhundVaultExport,
} from "../store";
import { toast } from "./Toasts";
import { Download, KeyRound, Lock, Pencil, Trash2, Unlock, Upload, X } from "lucide-react";

type EntryKind = "secret" | "config";
type ScopeType = "global" | "server";

const EMPTY_FORM = {
  id: "",
  key: "",
  kind: "secret" as EntryKind,
  scopeType: "global" as ScopeType,
  scopeServerId: "",
  description: "",
  value: "",
};

export function VaultManagerDialog() {
  const t = useGT();
  const showVaultManager = useAppStore((s) => s.showVaultManager);
  const setShowVaultManager = useAppStore((s) => s.setShowVaultManager);
  const pendingVaultRefs = useAppStore((s) => s.pendingVaultRefs);
  const { hasVault, isUnlocked, isRemembered, entries } = useVaultStore();
  const servers = useServersStore((s) => s.servers);

  const [passphrase, setPassphrase] = useState("");
  const [confirmPassphrase, setConfirmPassphrase] = useState("");
  const [rememberOnDevice, setRememberOnDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const serverNames = useMemo(
    () => Object.fromEntries(servers.map((server) => [server.id, server.name])),
    [servers],
  );

  useEffect(() => {
    if (!showVaultManager) return;
    void refreshVaultInApp();
  }, [showVaultManager]);

  useEffect(() => {
    if (!showVaultManager) return;
    setRememberOnDevice(isRemembered);
  }, [showVaultManager, isRemembered]);

  useEffect(() => {
    if (!showVaultManager || !isUnlocked || pendingVaultRefs.length === 0) return;
    void (async () => {
      const created = await materializePendingVaultRefsInApp();
      if (created > 0) {
        toast(
          created === 1
            ? "1 missing shared secret/config entry was created."
            : `${created} missing shared secret/config entries were created.`,
          "success",
        );
      }
    })();
  }, [showVaultManager, isUnlocked, pendingVaultRefs.length]);

  if (!showVaultManager) return null;

  const close = () => setShowVaultManager(false);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(false);
  };

  const handleCreateVault = async () => {
    if (!passphrase.trim()) {
      toast("Passphrase required", "error");
      return;
    }
    if (passphrase !== confirmPassphrase) {
      toast("Passphrases do not match", "error");
      return;
    }

    setBusy(true);
    try {
      await createVaultInApp(passphrase, rememberOnDevice);
      setPassphrase("");
      setConfirmPassphrase("");
      toast("Vault created and unlocked", "success");
    } catch (error) {
      if (error instanceof Error && error.message === "Vault already exists") {
        await refreshVaultInApp();
      }
      toast(error instanceof Error ? error.message : "Failed to create vault", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleUnlockVault = async () => {
    setBusy(true);
    try {
      await unlockVaultInApp(passphrase, rememberOnDevice);
      setPassphrase("");
      toast("Vault unlocked", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to unlock vault", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleLockVault = () => {
    lockVaultInApp();
    resetForm();
    toast("Vault locked", "success");
  };

  const handleForgetRemembered = async () => {
    setBusy(true);
    try {
      await forgetRememberedVaultInApp();
      setRememberOnDevice(false);
      toast("Device remember cleared", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to clear device remember", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = async (id: string) => {
    setBusy(true);
    try {
      const entry = entries.find((item) => item.id === id);
      if (!entry) return;
      const value = await readVaultValue(id);
      setForm({
        id: entry.id,
        key: entry.key,
        kind: entry.kind,
        scopeType: entry.scopeType,
        scopeServerId: entry.scopeServerId ?? "",
        description: entry.description,
        value,
      });
      setEditing(true);
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to load secret", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!form.key.trim()) {
      toast("Key required", "error");
      return;
    }
    if (form.scopeType === "server" && !form.scopeServerId) {
      toast("Select a server scope", "error");
      return;
    }

    setBusy(true);
    try {
      await saveVaultEntryInApp({
        ...(form.id ? { id: form.id } : {}),
        key: form.key,
        kind: form.kind,
        scopeType: form.scopeType,
        ...(form.scopeType === "server" ? { scopeServerId: form.scopeServerId } : {}),
        description: form.description,
        value: form.value,
      });
      resetForm();
      toast(editing ? "Entry updated" : "Entry created", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to save entry", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this vault entry?")) return;
    setBusy(true);
    try {
      await deleteVaultEntryInApp(id);
      if (form.id === id) resetForm();
      toast("Entry deleted", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to delete entry", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleExportVault = async () => {
    setBusy(true);
    try {
      const data = await exportVault();
      downloadJson(data, `spuerhund-vault-${new Date().toISOString().slice(0, 10)}.json`);
      toast("Encrypted vault exported", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to export vault", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleImportClick = () => importRef.current?.click();

  const handleImportVault = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setBusy(true);
    try {
      const parsed = JSON.parse(await file.text()) as SpuerhundVaultExport;
      await importVault(parsed);
      resetForm();
      setPassphrase("");
      setConfirmPassphrase("");
      toast("Vault imported. Unlock it with the original passphrase.", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to import vault", "error");
    } finally {
      setBusy(false);
    }
  };

  const entryCountLabel = entries.length === 1 ? t("1 entry") : t("{count} entries", { count: entries.length });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(event) => event.target === event.currentTarget && close()}
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-surface-raised shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-warning-bg text-warning">
              <KeyRound size={16} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-zinc-100">{t("Secrets & Config")}</h2>
              <p className="text-xs text-zinc-500">
                {t("Browser-local encrypted vault with server-scoped overrides and runtime references.")}
              </p>
            </div>
          </div>
          <button
            onClick={close}
            className="rounded p-1.5 text-zinc-500 hover:bg-surface hover:text-zinc-300"
            title={t("Close")}
          >
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5">
          <div className="mb-5 rounded-xl border border-border bg-surface px-4 py-3 text-xs text-zinc-400">
            <p>
              {t("Use")} <code>{"{{secret:key}}"}</code> {t("or")} <code>{"{{config:key}}"}</code> {t("in static auth/header values.")}
            </p>
            <p className="mt-1">
              {t("Custom header scripts can call")} <code>await secret("key")</code> {t("and")} <code>await config("key")</code>.
            </p>
          </div>

          {pendingVaultRefs.length > 0 && (
            <div className="mb-5 rounded-xl border border-warning/30 bg-warning-bg px-4 py-3 text-xs text-warning">
              <p className="font-medium">
                Shared server requires local secrets/config values.
              </p>
              <p className="mt-1">
                {pendingVaultRefs.map((entry) => `${entry.kind}:${entry.key}`).join(", ")}
              </p>
              {!hasVault && <p className="mt-2">Create a local vault first. The missing entries will be added automatically right after that.</p>}
              {hasVault && !isUnlocked && <p className="mt-2">Unlock the vault. The missing entries will be added automatically right after unlock.</p>}
            </div>
          )}

          {!hasVault && (
            <div className="mx-auto max-w-lg rounded-xl border border-border bg-surface px-5 py-5">
              <h3 className="text-sm font-semibold text-zinc-100">{t("Create local vault")}</h3>
              <p className="mt-1 text-xs text-zinc-500">
                The passphrase never leaves this browser session. Workspace exports do not include vault values.
              </p>
              <div className="mt-4 space-y-3">
                <input
                  type="password"
                  value={passphrase}
                  onChange={(event) => setPassphrase(event.target.value)}
                  placeholder={t("Vault passphrase")}
                  autoFocus
                  className="w-full rounded bg-surface-raised px-3 py-2 text-sm text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                />
                <input
                  type="password"
                  value={confirmPassphrase}
                  onChange={(event) => setConfirmPassphrase(event.target.value)}
                  placeholder={t("Confirm passphrase")}
                  className="w-full rounded bg-surface-raised px-3 py-2 text-sm text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                />
                <label className="flex items-center gap-2 text-xs text-zinc-400">
                  <input
                    type="checkbox"
                    checked={rememberOnDevice}
                    onChange={(event) => setRememberOnDevice(event.target.checked)}
                    className="h-4 w-4 rounded border-border bg-surface text-brand focus:ring-accent"
                  />
                  <span>{t("Remember on this device")}</span>
                </label>
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={handleImportClick}
                    className="flex items-center gap-2 rounded px-3 py-2 text-xs text-zinc-400 ring-1 ring-border hover:bg-surface-raised hover:text-zinc-200"
                  >
                    <Upload size={12} />
                    {t("Import Encrypted Vault")}
                  </button>
                  <button
                    onClick={handleCreateVault}
                    disabled={busy}
                    className="rounded bg-brand px-4 py-2 text-xs font-semibold text-zinc-900 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("Create Vault")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {hasVault && !isUnlocked && (
            <div className="mx-auto max-w-lg rounded-xl border border-border bg-surface px-5 py-5">
              <h3 className="text-sm font-semibold text-zinc-100">{t("Unlock vault")}</h3>
              <p className="mt-1 text-xs text-zinc-500">
                Secrets are encrypted locally. Unlock the vault to resolve references and edit entries.
              </p>
              <div className="mt-4 space-y-3">
                <input
                  type="password"
                  value={passphrase}
                  onChange={(event) => setPassphrase(event.target.value)}
                  placeholder={t("Vault passphrase")}
                  autoFocus
                  className="w-full rounded bg-surface-raised px-3 py-2 text-sm text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                />
                <label className="flex items-center gap-2 text-xs text-zinc-400">
                  <input
                    type="checkbox"
                    checked={rememberOnDevice}
                    onChange={(event) => setRememberOnDevice(event.target.checked)}
                    className="h-4 w-4 rounded border-border bg-surface text-brand focus:ring-accent"
                  />
                  <span>{t("Remember on this device")}</span>
                </label>
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={handleImportClick}
                    className="flex items-center gap-2 rounded px-3 py-2 text-xs text-zinc-400 ring-1 ring-border hover:bg-surface-raised hover:text-zinc-200"
                  >
                    <Upload size={12} />
                    {t("Import Encrypted Vault")}
                  </button>
                  <button
                    onClick={handleUnlockVault}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded bg-brand px-4 py-2 text-xs font-semibold text-zinc-900 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Unlock size={12} />
                    {t("Unlock")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {hasVault && isUnlocked && (
            <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-xl border border-border bg-surface">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-100">{t("Entries")}</h3>
                    <p className="text-[11px] text-zinc-500">{entryCountLabel}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isRemembered && (
                      <button
                        onClick={() => void handleForgetRemembered()}
                        className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-[11px] text-zinc-400 ring-1 ring-border hover:bg-surface-raised hover:text-zinc-200"
                      >
                        {t("Forget Device")}
                      </button>
                    )}
                    <button
                      onClick={handleExportVault}
                      className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-[11px] text-zinc-400 ring-1 ring-border hover:bg-surface-raised hover:text-zinc-200"
                    >
                      <Download size={11} />
                      {t("Export Vault")}
                    </button>
                    <button
                      onClick={handleImportClick}
                      className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-[11px] text-zinc-400 ring-1 ring-border hover:bg-surface-raised hover:text-zinc-200"
                    >
                      <Upload size={11} />
                      {t("Import")}
                    </button>
                    <button
                      onClick={handleLockVault}
                      className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-[11px] text-zinc-400 ring-1 ring-border hover:bg-surface-raised hover:text-zinc-200"
                    >
                      <Lock size={11} />
                      {t("Lock")}
                    </button>
                  </div>
                </div>

                <div className="overflow-hidden">
                  {entries.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-zinc-500">{t("No secrets or configs yet.")}</div>
                  ) : (
                    <div className="divide-y divide-border">
                      {entries.map((entry) => (
                        <div key={entry.id} className="flex items-center gap-3 px-4 py-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                entry.kind === "secret"
                                  ? "bg-danger-bg text-danger"
                                  : "bg-info-bg text-info"
                              }`}>
                                {entry.kind}
                              </span>
                              <span className="truncate text-sm font-medium text-zinc-100">{entry.key}</span>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
                              <span>{entry.scopeType === "global" ? "Global" : `Server: ${serverNames[entry.scopeServerId ?? ""] ?? entry.scopeServerId}`}</span>
                              {entry.description && <span className="truncate">{entry.description}</span>}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              onClick={() => void handleEdit(entry.id)}
                              className="rounded p-1 text-zinc-500 hover:text-zinc-200"
                              title={t("Edit entry")}
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              onClick={() => void handleDelete(entry.id)}
                              className="rounded p-1 text-zinc-500 hover:text-danger"
                              title={t("Delete entry")}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-surface">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-100">
                      {editing ? t("Edit entry") : t("New entry")}
                    </h3>
                    <p className="text-[11px] text-zinc-500">
                      {t("Keep secrets out of auth JSON and use references instead.")}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 px-4 py-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Kind")}</label>
                      <select
                        value={form.kind}
                        onChange={(event) => setForm((current) => ({ ...current, kind: event.target.value as EntryKind }))}
                        className="w-full rounded bg-surface-raised px-3 py-2 text-sm text-zinc-200 outline-none ring-1 ring-border focus:ring-accent"
                      >
                        <option value="secret">{t("Secret")}</option>
                        <option value="config">{t("Config")}</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Scope")}</label>
                      <select
                        value={form.scopeType}
                        onChange={(event) => setForm((current) => ({ ...current, scopeType: event.target.value as ScopeType }))}
                        className="w-full rounded bg-surface-raised px-3 py-2 text-sm text-zinc-200 outline-none ring-1 ring-border focus:ring-accent"
                      >
                        <option value="global">{t("Global")}</option>
                        <option value="server">{t("Server")}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Key")}</label>
                    <input
                      type="text"
                      value={form.key}
                      onChange={(event) => setForm((current) => ({ ...current, key: event.target.value }))}
                      placeholder={t("sympathy.client_secret")}
                      className="w-full rounded bg-surface-raised px-3 py-2 text-sm font-mono text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    />
                  </div>

                  {form.scopeType === "server" && (
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Server")}</label>
                      <select
                        value={form.scopeServerId}
                        onChange={(event) => setForm((current) => ({ ...current, scopeServerId: event.target.value }))}
                        className="w-full rounded bg-surface-raised px-3 py-2 text-sm text-zinc-200 outline-none ring-1 ring-border focus:ring-accent"
                      >
                        <option value="">{t("Select server...")}</option>
                        {servers.map((server) => (
                          <option key={server.id} value={server.id}>{server.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Description")}</label>
                    <input
                      type="text"
                      value={form.description}
                      onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                      placeholder={t("Optional note")}
                      className="w-full rounded bg-surface-raised px-3 py-2 text-sm text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500">{t("Value")}</label>
                    <textarea
                      value={form.value}
                      onChange={(event) => setForm((current) => ({ ...current, value: event.target.value }))}
                      rows={6}
                      spellCheck={false}
                      placeholder={form.kind === "secret" ? t("Paste secret value") : t("Config value")}
                      className="w-full resize-none rounded bg-surface-raised px-3 py-2 font-mono text-xs text-zinc-200 outline-none ring-1 ring-border placeholder:text-zinc-700 focus:ring-accent"
                    />
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={resetForm}
                      className="rounded px-3 py-2 text-xs text-zinc-400 hover:bg-surface-raised hover:text-zinc-200"
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={busy}
                      className="rounded bg-brand px-4 py-2 text-xs font-semibold text-zinc-900 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {editing ? t("Update Entry") : t("Save Entry")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <input
            ref={importRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImportVault}
          />
        </div>
      </div>
    </div>
  );
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
